import { useState, useEffect, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Delete } from 'lucide-react';
import { authApi, StaffLoginEntryDto } from '@/api/authApi';
import { useAuthStore, clearRememberedWorkspace } from '@/store/authStore';
import { useTenantStore } from '@/store/tenantStore';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { ForceChangePasswordPage } from './ForceChangePasswordPage';

const ROLE_BADGE: Record<string, 'default' | 'success' | 'info' | 'neutral' | 'warning'> = {
  Owner:          'default',
  Manager:        'success',
  Technician:     'info',
  SeniorTechnician: 'info',
  ServiceAdvisor: 'warning',
  PartClerk:      'neutral',
  Receptionist:   'neutral',
};

function getInitials(name: string) {
  const parts = name.trim().split(' ');
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : name.slice(0, 2).toUpperCase();
}

type ModalMode = 'pin' | 'password' | 'owner' | null;

export default function StaffLoginPage() {
  const navigate = useNavigate();
  const { setStaffSession, clientType } = useAuthStore();
  const { displayName: ownerDisplayName, companyName } = useTenantStore();

  const [staff, setStaff] = useState<StaffLoginEntryDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const [selected, setSelected] = useState<StaffLoginEntryDto | null>(null);
  const [modalMode, setModalMode] = useState<ModalMode>(null);

  // PIN state
  const [pin, setPin] = useState('');

  // Password state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [failCount, setFailCount] = useState(0);

  // Force password change flow
  const [forceChangePassword, setForceChangePassword] = useState(false);
  const [tempPassword, setTempPassword] = useState('');

  // Owner modal state
  const [ownerPassword, setOwnerPassword] = useState('');
  const [ownerError, setOwnerError] = useState<string | null>(null);
  const [ownerSubmitting, setOwnerSubmitting] = useState(false);

  useEffect(() => {
    if (!useAuthStore.getState().token) {
      navigate('/workspace-login');
      return;
    }
    authApi.listStaffForLogin()
      .then(setStaff)
      .catch((err) => {
        if (err?.response?.status === 401) {
          navigate('/workspace-login');
        } else {
          setFetchError('Could not load staff list. Check your connection and try again.');
        }
      })
      .finally(() => setLoading(false));
  }, []);

  function openCard(member: StaffLoginEntryDto) {
    setSelected(member);
    setAuthError(null);
    setFailCount(0);
    setPin('');
    setUsername('');
    setPassword('');
    setModalMode(member.requiresPin ? 'pin' : 'password');
  }

  function closeModal() {
    setModalMode(null);
    setSelected(null);
    setAuthError(null);
    setPin('');
    setUsername('');
    setPassword('');
    setOwnerPassword('');
    setOwnerError(null);
  }

  async function submitAuth() {
    if (!selected) return;
    setSubmitting(true);
    setAuthError(null);
    try {
      const params = modalMode === 'pin'
        ? { staffId: selected.staffId, pin }
        : { username: username.trim(), password };

      const result = await authApi.staffLogin(params);
      setStaffSession(result.token, result.staffId, result.role, result.fullName);

      // Check if password change is required
      if (result.mustChangePassword) {
        setTempPassword(modalMode === 'password' ? password : '');
        setForceChangePassword(true);
        setModalMode(null);
      } else {
        navigate('/dashboard');
      }
    } catch (err: unknown) {
      const ax = err as { response?: { status: number; data?: { error?: string } } };
      const status = ax?.response?.status;
      const nextFails = failCount + 1;
      setFailCount(nextFails);
      setPin('');
      setPassword('');

      if (ax?.response?.data?.error === 'STAFF_INACTIVE') {
        setAuthError('This account is inactive or cannot log in. Contact your shop owner.');
      } else if (status === 401) {
        if (nextFails >= 3) {
          setAuthError('Incorrect credentials. Need help? Contact your shop owner to reset your credentials.');
        } else {
          setAuthError(modalMode === 'pin' ? 'Incorrect PIN. Try again.' : 'Incorrect username or password. Try again.');
        }
      } else {
        setAuthError('An error occurred. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  function handlePinKey(digit: string) {
    if (pin.length < 6) setPin((p) => p + digit);
  }

  function handlePinBackspace() {
    setPin((p) => p.slice(0, -1));
  }

  async function handlePinSubmit() {
    if (pin.length < 4) return;
    await submitAuth();
  }

  async function handlePasswordSubmit(e: FormEvent) {
    e.preventDefault();
    await submitAuth();
  }

  async function handleOwnerSubmit(e: FormEvent) {
    e.preventDefault();
    setOwnerSubmitting(true);
    setOwnerError(null);
    try {
      const result = await authApi.reAuthenticate(ownerPassword);
      const label = result.staffName || ownerDisplayName || companyName || 'Owner';
      setStaffSession(result.staffToken ?? null, result.staffId ?? null, 'Owner', label);
      navigate('/dashboard');
    } catch (err: unknown) {
      const ax = err as { response?: { status: number } };
      if (ax?.response?.status === 401) {
        setOwnerError('Incorrect password. Try again.');
      } else {
        setOwnerError('An error occurred. Please try again.');
      }
    } finally {
      setOwnerSubmitting(false);
    }
  }

  function handleSwitchWorkspace() {
    clearRememberedWorkspace();
    navigate('/login');
  }

  // Show force password change page if required
  if (forceChangePassword) {
    return (
      <ForceChangePasswordPage
        tempPassword={tempPassword}
        onSuccess={() => navigate('/dashboard')}
      />
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <img src="/logo.png" alt="WrenchLab" className="h-16 w-auto object-contain mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-neutral-900">Who's working?</h1>
          <p className="text-sm text-neutral-500 mt-1">Select your name to continue</p>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex justify-center py-16">
            <div className="h-8 w-8 rounded-full border-4 border-primary-600 border-t-transparent animate-spin" />
          </div>
        )}

        {/* Fetch error */}
        {fetchError && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-wl px-4 py-3 mb-6 text-center">
            {fetchError}
          </div>
        )}

        {/* Staff grid */}
        {!loading && !fetchError && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {/* Owner tile — always visible */}
              <button
                onClick={() => { setOwnerPassword(''); setOwnerError(null); setModalMode('owner'); }}
                className="wl-card p-5 flex flex-col items-center gap-3 hover:shadow-md hover:border-primary-300 transition-all text-left group"
              >
                <div className="w-14 h-14 rounded-full bg-primary-100 group-hover:bg-primary-200 flex items-center justify-center transition-colors">
                  <span className="text-lg font-bold text-primary-700">
                    {getInitials(ownerDisplayName || companyName || 'Owner')}
                  </span>
                </div>
                <div className="text-center">
                  <p className="text-sm font-semibold text-neutral-900 leading-tight">
                    {ownerDisplayName || companyName || 'Owner'}
                  </p>
                  <div className="mt-1.5">
                    <Badge variant="default">Owner</Badge>
                  </div>
                </div>
              </button>

              {staff.map((member) => (
                <button
                  key={member.staffId}
                  onClick={() => openCard(member)}
                  className="wl-card p-5 flex flex-col items-center gap-3 hover:shadow-md hover:border-primary-300 transition-all text-left group"
                >
                  <div className="w-14 h-14 rounded-full bg-primary-100 group-hover:bg-primary-200 flex items-center justify-center transition-colors">
                    <span className="text-lg font-bold text-primary-700">{getInitials(member.fullName)}</span>
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-semibold text-neutral-900 leading-tight">{member.fullName}</p>
                    <div className="mt-1.5">
                      <Badge variant={ROLE_BADGE[member.role] ?? 'neutral'}>
                        {member.role === 'SeniorTechnician' ? 'Sr. Tech' : member.role}
                      </Badge>
                    </div>
                    {member.requiresPin && (
                      <p className="text-[10px] text-neutral-400 mt-1">PIN required</p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </>
        )}

        {/* Navigation */}
        <div className="text-center mt-8 flex items-center justify-center gap-4">
          <button
            type="button"
            onClick={handleSwitchWorkspace}
            className="text-xs text-neutral-400 hover:text-neutral-600 hover:underline transition-colors"
          >
            Switch workspace
          </button>
          <span className="text-neutral-300">•</span>
          <a
            href="https://wrenchlab.net"
            className="text-xs text-neutral-400 hover:text-neutral-600 hover:underline transition-colors"
          >
            WrenchLab home
          </a>
        </div>
      </div>

      {/* PIN Modal */}
      <Modal
        open={modalMode === 'pin'}
        title={`Enter PIN — ${selected?.fullName ?? ''}`}
        onClose={closeModal}
        size="sm"
      >
        <div className="flex flex-col items-center gap-4">
          {/* PIN display */}
          <div className="flex gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-colors ${
                  i < pin.length
                    ? 'bg-primary-600 border-primary-600'
                    : 'bg-neutral-100 border-neutral-300'
                }`}
              >
                {i < pin.length && <div className="w-2.5 h-2.5 rounded-full bg-white" />}
              </div>
            ))}
          </div>

          {authError && (
            <p className="text-sm text-red-600 text-center">{authError}</p>
          )}

          {/* Numeric keypad */}
          <div className="grid grid-cols-3 gap-2 w-48">
            {['1','2','3','4','5','6','7','8','9'].map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => handlePinKey(d)}
                className="h-12 rounded-xl bg-neutral-100 hover:bg-neutral-200 text-neutral-900 font-semibold text-lg transition-colors"
              >
                {d}
              </button>
            ))}
            <div /> {/* spacer */}
            <button
              type="button"
              onClick={() => handlePinKey('0')}
              className="h-12 rounded-xl bg-neutral-100 hover:bg-neutral-200 text-neutral-900 font-semibold text-lg transition-colors"
            >
              0
            </button>
            <button
              type="button"
              onClick={handlePinBackspace}
              className="h-12 rounded-xl bg-neutral-100 hover:bg-neutral-200 text-neutral-500 transition-colors flex items-center justify-center"
            >
              <Delete className="h-5 w-5" />
            </button>
          </div>

          <Button
            variant="primary"
            className="w-full"
            onClick={handlePinSubmit}
            loading={submitting}
            disabled={pin.length < 4}
          >
            Confirm
          </Button>
        </div>
      </Modal>

      {/* Password Modal */}
      <Modal
        open={modalMode === 'password'}
        title={`Sign in — ${selected?.fullName ?? ''}`}
        onClose={closeModal}
        size="sm"
      >
        <form onSubmit={handlePasswordSubmit} className="space-y-4">
          {authError && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-wl px-3 py-2">
              {authError}
            </div>
          )}
          <Input
            label="Username"
            type="text"
            placeholder="your.username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            autoFocus
            required
          />
          <Input
            label="Password"
            type="password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
          <div className="flex gap-2 pt-1">
            <Button type="button" variant="ghost" className="flex-1" onClick={closeModal}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" className="flex-1" loading={submitting}>
              Sign In
            </Button>
          </div>
        </form>
      </Modal>

      {/* Owner Modal */}
      <Modal
        open={modalMode === 'owner'}
        title="Confirm your identity"
        onClose={closeModal}
        size="sm"
      >
        <form onSubmit={handleOwnerSubmit} className="space-y-4">
          {ownerError && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-wl px-3 py-2">
              {ownerError}
            </div>
          )}
          <Input
            label="Password"
            type="password"
            placeholder="••••••••"
            value={ownerPassword}
            onChange={(e) => setOwnerPassword(e.target.value)}
            autoComplete="current-password"
            autoFocus
            required
          />
          <div className="flex gap-2 pt-1">
            <Button type="button" variant="ghost" className="flex-1" onClick={closeModal}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" className="flex-1" loading={ownerSubmitting}>
              Confirm
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
